import { FC, useCallback, useId, useState } from 'react';
import CoverageSearch from 'components/CoverageSearch/CoverageSearch';
import { Panel } from '@hologram-dimension/panel';
import useBillingRates from 'api/billing/useBillingRates';
import useSimDetails from 'api/sims/useSimDetails';
import useSimProfiles from 'api/sims/useSimProfiles';
import { Button } from '@hologram-dimension/button';
import useAlgoliaFilter from 'components/CoverageSearch/useAlgoliaFilter';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import { FPLMNArticle, NewSupportTicket } from 'utils/constants/links';
import { EnumSimInventoryExportType, exportTypeToLabel } from 'types/common';
import { DropdownPanel } from '@hologram-dimension/dropdown-panel';
import useSimBilling from 'api/sims/useSimBilling';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useSimStatuses from 'api/sims/useSimStatuses';
import { EnumsSimNetworkState } from 'types/__generated__/SimInventoryApi';
import exportCoverageData from 'utils/coverage/exportCoverageData';
import useCoverageProfiles from 'components/CoverageSearch/useCoverageProfiles';
import SimCoveragePanelHeader from './SimCoveragePanelHeader';
import styles from './SimCoveragePanel.module.css';

interface SimCoveragePanelProps {
  hologramSimId?: HologramSimId;
}

const SimCoveragePanel: FC<SimCoveragePanelProps> = ({ hologramSimId }) => {
  const {
    simDetails,
    isLoading: isSimDetailsLoading,
    error: simDetailsError,
  } = useSimDetails(hologramSimId);
  const {
    simProfiles,
    isLoading: isSimProfilesLoading,
    error: simProfilesError,
  } = useSimProfiles(hologramSimId);
  const {
    simBilling,
    error: simBillingError,
    isLoading: isSimBillingLoading,
  } = useSimBilling(hologramSimId);
  const {
    billingRates,
    isLoading: isBillingRatesLoading,
    error: billingRatesError,
  } = useBillingRates(simDetails.organizationId);
  const {
    networkStatusGroups,
    isLoading: isSimStatusesLoading,
    error: simStatusesError,
  } = useSimStatuses();
  const isLoading =
    isSimDetailsLoading ||
    isSimProfilesLoading ||
    isBillingRatesLoading ||
    isSimBillingLoading ||
    isSimStatusesLoading;
  const error = simDetailsError || simProfilesError || simBillingError || simStatusesError; // we will handle billing rates error differently

  const { sendAnalyticsEvent, sendAnalyticsError } = useAnalytics();
  const filters = useAlgoliaFilter({ simProfiles });
  const profiles = useCoverageProfiles({ simProfiles });
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showExport, setShowExport] = useState<boolean>(false);
  const onExportClick = useCallback(() => {
    setShowExport((prev) => !prev);
  }, []);
  const exportButtonId = useId();

  const handleExport = async (exportType: EnumSimInventoryExportType) => {
    setIsExporting(true);
    onExportClick();

    try {
      const exportFn = await exportCoverageData({
        filters,
        profiles,
        hologramSimId,
        billingRates,
        simBilling,
        onSuccess: (type) => {
          if (sendAnalyticsEvent) {
            sendAnalyticsEvent(`Network Coverage - Export Networks - Export Networks success`, {
              exportType: exportTypeToLabel(type),
            });
          }
        },
        onError: (err) => {
          if (sendAnalyticsError) {
            sendAnalyticsError('Export Network Coverage error', err);
          }
        },
      });

      await exportFn(exportType);
      return setIsExporting(false);
    } catch (e) {
      setIsExporting(false);
      return false;
    }
  };

  const panelTitle = 'Network coverage and rates';
  if (!hologramSimId) {
    return (
      <Panel header={panelTitle} isLoading={isLoading}>
        <PanelNotification label="No SIM selected" variant="error">
          Please select a SIM to view network coverage and rates
        </PanelNotification>
      </Panel>
    );
  }

  if (isSimDetailsLoading) {
    return (
      <Panel header={panelTitle} isLoading={isLoading}>
        <PanelNotification>Loading coverage information...</PanelNotification>
      </Panel>
    );
  }

  if (error || !simDetails.hologramSimId) {
    return (
      <Panel header={panelTitle} isLoading={isLoading}>
        <PanelNotification
          label="No coverage information found"
          variant="error"
          actions={
            <Button
              type="button"
              variant="secondary"
              iconEnd="ExternalLink"
              href={NewSupportTicket}
              target="_blank"
            >
              Contact support
            </Button>
          }
        >
          We could not retrieve network coverage information for this SIM. If this isn’t fixed after
          reloading this page, please reach out to our support team.
        </PanelNotification>
      </Panel>
    );
  }

  // If the SIM is in predeployment and doesn't have a plan, show a message
  if (
    networkStatusGroups?.predeployment?.includes(
      simDetails.networkStatus as EnumsSimNetworkState
    ) &&
    !simBilling.planId
  ) {
    return (
      <Panel header={panelTitle} isLoading={isLoading}>
        <PanelNotification label="This SIM does not have a data plan yet" icon="CircleInfo">
          Once a data plan has been assigned to this SIM, coverage and rate information will be
          available here.
        </PanelNotification>
      </Panel>
    );
  }

  return (
    <Panel
      header={panelTitle}
      headerActions={
        <DropdownPanel
          content={[
            {
              label: `Export coverage and rates`,
              items: [
                {
                  children: 'Export to XLSX (Excel)',
                  iconStart: 'DownloadTray',
                  onClick: () => handleExport(EnumSimInventoryExportType.XLSX),
                },
                {
                  children: 'Export to CSV',
                  iconStart: 'DownloadTray',
                  onClick: () => handleExport(EnumSimInventoryExportType.CSV),
                },
              ],
            },
          ]}
          open={showExport}
          onOpenChange={setShowExport}
          className={styles.exportDropdown}
        >
          <Button
            iconEnd={showExport ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
            variant="secondary"
            onClick={onExportClick}
            disabled={!!error || isLoading || !!billingRatesError}
            id={exportButtonId}
            loading={isExporting}
          >
            Export
          </Button>
        </DropdownPanel>
      }
      footer={
        <span>
          Networks you can connect to are determined by the enabled profile on your SIM. You can{' '}
          <a className={styles.footerLink} href={FPLMNArticle}>
            forbid or prefer networks
          </a>{' '}
          on your SIM using FPLMN/OPLMN terminal commands. Additional fields like TADIG, MCC, MNC,
          and UNC are available in the file export.
        </span>
      }
      footerVariant="alternative"
      noContentPadding
      isLoading={isLoading}
    >
      <SimCoveragePanelHeader hologramSimId={hologramSimId} />

      <div className={styles.coverageWrapper}>
        <CoverageSearch hologramSimId={hologramSimId} />
      </div>
    </Panel>
  );
};
export default SimCoveragePanel;
